// Aging clock logic for pallets (FR16, FR17).
//
// Thresholds are per production flow (cold-chain limits differ by commodity).
// CITRUS (the default, anchored to PPECB):
//   Exports: 2-day (48h) hard limit — warn at 36h, critical at 42h.
//   Imports: 30-day hard limit      — warn at 22d, critical at 27d.
// BLUEBERRY is far more perishable (cool fast, ship within ~a day) — much
// shorter windows; see FLOW_AGE_POLICY. Once a pallet is DISPATCHED the clock
// stops — aging is only relevant while the fruit is in our custody.

import type { ProductionFlow } from '@/lib/useProductionFlow'

export type AgeBand = 'ok' | 'warning' | 'critical' | 'over'

export interface AgeStatus {
  band:       AgeBand
  hours:      number
  label:      string     // e.g. "36h old", "2d 4h old"
  badgeClass: string     // tailwind classes for badges
  borderClass: string    // tailwind border colour for cards
  pulse:      boolean    // true for critical+ → animate
}

// Warn / critical / hard-limit hours per channel, per production flow. The
// CITRUS numbers are exactly the previous hardcoded thresholds, so every
// existing caller (which doesn't pass a flow → defaults to CITRUS) is
// byte-identical. BLUEBERRY values are sensible placeholders to tune with the
// operator — blueberries cool fast and ship within ~a day.
interface FlowAgePolicy {
  export: { warn: number; crit: number; limit: number }
  import: { warn: number; crit: number; limit: number }
}

export const FLOW_AGE_POLICY: Record<ProductionFlow, FlowAgePolicy> = {
  CITRUS: {
    export: { warn: 36,      crit: 42,      limit: 48      },
    import: { warn: 22 * 24, crit: 27 * 24, limit: 30 * 24 },
  },
  BLUEBERRY: {
    export: { warn: 12,      crit: 18,      limit: 24      },
    import: { warn: 24,      crit: 36,      limit: 48      },
  },
}

// Fallback thresholds for a flow with NO in-code policy (a new commodity defined
// only as a commodity_templates row — P2/P4). It is TIGHT (the blueberry berry
// window), NOT citrus: a new commodity is far likelier to be a perishable than a
// 30-day citrus import, so the safe assumption is the short window. A flow that
// genuinely warrants longer limits gets its own FLOW_AGE_POLICY entry (or, later,
// per-flow cold-chain columns on commodity_templates — see STAGE-PIPELINE-PLAN).
export const SAFE_AGE_POLICY: FlowAgePolicy = {
  export: { warn: 12, crit: 18, limit: 24 },
  import: { warn: 24, crit: 36, limit: 48 },
}

/**
 * The hour figure the Home loading-bay pill names (F16 UF-10, UF-11). The pulse
 * RPC bands each pallet by its own fruit's export limit (flow_age_limits, mig
 * 20261047400000, the server twin of FLOW_AGE_POLICY), so the pill may name the
 * number when every flow this packhouse runs shares one export limit (citrus
 * 48h, berry 24h). Flows that disagree, or none known yet, get the unit-less
 * "limit" rather than a figure that is only true for part of the count.
 */
export function pulseExportLimitLabel(flowKeys: string[]): string {
  if (flowKeys.length === 0) return 'limit'
  const limits = new Set(flowKeys.map(k => (FLOW_AGE_POLICY[k] ?? SAFE_AGE_POLICY).export.limit))
  return limits.size === 1 ? `${[...limits][0]}h` : 'limit'
}

function formatAge(hours: number): string {
  // Round the TOTAL, then split. Rounding the remainder after flooring the
  // days reached 24: 575.7h printed "23d 24h old" (PM-CST-09, PB-11).
  const h = Math.round(hours)
  if (h < 48) return `${h}h`
  const days = Math.floor(h / 24)
  const rem  = h - days * 24
  return rem === 0 ? `${days}d` : `${days}d ${rem}h`
}

export function getAgeStatus(
  packedAt: string,
  isExport: boolean,
  active: boolean,
  flow: ProductionFlow = 'CITRUS',
): AgeStatus {
  const hours  = Math.max(0, (Date.now() - new Date(packedAt).getTime()) / 3_600_000)
  const policy = FLOW_AGE_POLICY[flow] ?? SAFE_AGE_POLICY
  const band   = isExport ? policy.export : policy.import
  const warn  = band.warn
  const crit  = band.crit
  const limit = band.limit

  const label = `${formatAge(hours)} old`

  // Dispatched or otherwise inactive pallets always show as ok — clock has stopped.
  if (!active) {
    return { band: 'ok', hours, label, badgeClass: 'bg-gray-700 text-gray-300', borderClass: '', pulse: false }
  }

  if (hours >= limit) {
    return {
      band: 'over', hours, label: `${label} — over limit`,
      badgeClass: 'bg-red-700 text-white',
      borderClass: 'border-red-500',
      pulse: true,
    }
  }
  if (hours >= crit) {
    return {
      band: 'critical', hours, label,
      badgeClass: 'bg-red-900/60 text-red-200',
      borderClass: 'border-red-600',
      pulse: true,
    }
  }
  if (hours >= warn) {
    return {
      band: 'warning', hours, label,
      badgeClass: 'bg-amber-900/60 text-amber-200',
      borderClass: 'border-amber-600',
      pulse: false,
    }
  }
  return { band: 'ok', hours, label, badgeClass: 'bg-gray-700 text-gray-300', borderClass: '', pulse: false }
}

// Pallet-like shape we need for aging rollup.
//   stocked_at — when the pallet was physically placed into the coldroom
//     (status → IN_COLDROOM). This is the age anchor we WANT: packed_at
//     defaults to NOW() at pallet/shell creation, so a line-preset shell
//     created at the START of a long run reads as days old the moment it's
//     placed in cold, even though the fruit only just entered our custody.
//     Aging from stocked_at fixes that "fruit isn't that old" illusion.
//   born_date — set when the pallet was packed from a re-tipped bin; the
//     fruit's true origin, preserved through rebins. It still WINS when it
//     predates the placement/pack anchor, so a rebin can never hide genuinely
//     old fruit behind a fresh placement date.
export interface AgingPallet {
  packed_at:   string
  stocked_at?: string | null
  born_date?:  string | null
  status:      string
  pallet_type: string | null
  spec:        string | null
}

// Age anchor for a pallet's fruit. Prefer stocked_at (moment it entered cold
// custody); fall back to packed_at while it's still pre-cold (stocked_at null).
// born_date still wins when older, so rebinned old fruit stays flagged.
// Centralised so callers don't have to remember the rule.
export function effectiveAgeStart(p: { packed_at: string; stocked_at?: string | null; born_date?: string | null }): string {
  const base = p.stocked_at ?? p.packed_at
  return p.born_date && p.born_date < base ? p.born_date : base
}

export function isExportPallet(p: AgingPallet): boolean {
  return p.pallet_type === 'Export Pallet'
      || p.pallet_type === 'Export'
      || p.spec === 'Export'
}

export function isActivePallet(p: AgingPallet): boolean {
  return p.status !== 'DISPATCHED'
}

export interface AgeSummary {
  ok:       number
  warning:  number
  critical: number
  over:     number
  total:    number
}

export function summariseAging(pallets: AgingPallet[], flow: ProductionFlow = 'CITRUS'): AgeSummary {
  const summary: AgeSummary = { ok: 0, warning: 0, critical: 0, over: 0, total: 0 }
  for (const p of pallets) {
    if (!isActivePallet(p)) continue
    const status = getAgeStatus(effectiveAgeStart(p), isExportPallet(p), true, flow)
    summary[status.band]++
    summary.total++
  }
  return summary
}
